import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { getProductById } from '../api/apiendpoint';
import { BASE_URL } from '../api/apiendpoint';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart } = useCart();

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProductById(id);
        setProduct(data);
      } catch (error) {
        console.error("Failed to fetch product:", error);
        setError("Failed to load product. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(() => {
    if (product) {
      const existingItem = cartItems.find(item => item.id === product.id);
      setQuantity(existingItem ? existingItem.quantity : 0);
    }
  }, [product, cartItems]);

  const formatProductForCart = (product) => ({
    id: product.id,
    name: product.product_name,
    image: getProductImage(product),
    price: parseFloat(product.product_price_discount) || parseFloat(product.product_price) || 0,
    originalPrice: parseFloat(product.product_price) || 0,
    discountedPrice: parseFloat(product.product_price_discount) || 0,
    description: product.product_discription || 'No description available.',
  });

  const getProductImage = (product, index = 0) => {
    if (product.images?.[index]) {
      return `${BASE_URL}${product.images[index].product_img.replace(/^\/+/, '')}`;
    }
    return 'https://via.placeholder.com/400x400?text=No+Image';
  };

  const handleQuantityChange = (action) => {
    if (!product) return;

    if (action === 'add') {
      addToCart(formatProductForCart(product));
    } else if (action === 'remove' && quantity > 0) {
      removeFromCart(product.id);
    }
  };

  const handleOrderNow = () => {
    if (quantity === 0) {
      handleQuantityChange('add');
    }
    navigate('/cart');
  };

  const calculateDiscount = () => {
    if (product?.product_price && product?.product_price_discount) {
      const discount = ((product.product_price - product.product_price_discount) / product.product_price) * 100;
      return Math.round(discount);
    }
    return 0;
  };

  const currentPrice = product?.product_price_discount || product?.product_price || 0;
  const originalPrice = product?.product_price || 0;
  const totalPrice = (currentPrice * quantity).toFixed(2);
  const discountPercentage = calculateDiscount();

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="text-muted">Loading product details...</h5>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Oops!</h4>
          <p>{error || "Product not found."}</p>
          <hr />
          <button className="btn btn-outline-primary" onClick={() => navigate('/products')}>
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="container py-5"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <button className="btn btn-link p-0 text-decoration-none" onClick={() => navigate('/products')}>
              Products
            </button>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {product.product_name}
          </li>
        </ol>
      </nav>

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="position-relative">
            <motion.img
              key={selectedImage}
              src={getProductImage(product, selectedImage)}
              alt={product.product_name}
              className="img-fluid rounded-3 shadow-sm w-100"
              style={{
                objectFit: 'contain',
                maxHeight: '500px',
                width: '100%',
                height: 'auto',
                backgroundColor: '#f8f9fa'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            />

            {discountPercentage > 0 && (
              <div className="position-absolute top-0 start-0 m-3">
                <span className="badge bg-danger fs-6 px-3 py-2">
                  {discountPercentage}% OFF
                </span>
              </div>
            )}
          </div>

          {product.images && product.images.length > 1 && (
            <div className="d-flex gap-2 mt-3 flex-wrap">
              {product.images.map((image, index) => (
                <motion.button
                  key={index}
                  className={`btn p-0 border-0 ${selectedImage === index ? 'opacity-100' : 'opacity-75'}`}
                  onClick={() => setSelectedImage(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img
                    src={getProductImage(product, index)}
                    alt={`${product.product_name} ${index + 1}`}
                    className="rounded"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      border: selectedImage === index ? '2px solid #0d6efd' : '1px solid #dee2e6'
                    }}
                  />
                </motion.button>
              ))}
            </div>
          )}
        </div>

        <div className="col-lg-6">
          <div className="h-100 d-flex flex-column">
            <div className="mb-4">
              <h1 className="display-6 fw-bold text-dark mb-3">
                {product.product_name}
              </h1>
              <p className="text-muted fs-5 lh-base">
                {product.product_discription || 'No description available.'}
              </p>
            </div>

            <div className="mb-4">
              <div className="d-flex align-items-center gap-3 mb-2">
                <span className="display-5 fw-bold text-success">
                  ${currentPrice.toFixed(2)}
                </span>
                {discountPercentage > 0 && (
                  <span className="fs-4 text-muted text-decoration-line-through">
                    ${originalPrice.toFixed(2)}
                  </span>
                )}
              </div>
              {discountPercentage > 0 && (
                <small className="text-success fw-semibold">
                  You save ${(originalPrice - currentPrice).toFixed(2)} ({discountPercentage}% off)
                </small>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold mb-3">Quantity</label> <br />

              {quantity === 0 ? (
                <motion.button
                  type="button"
                  className="btn logo btn-lg px-4 py-2"
                  onClick={() => handleQuantityChange('add')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add to Cart
                </motion.button>
              ) : (
                <div className="d-flex align-items-center gap-3">
                  <motion.button
                    type="button"
                    className="btn btn-outline-danger btn-lg"
                    onClick={() => handleQuantityChange('remove')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ width: '45px', height: '45px' }}
                  >
                    −
                  </motion.button>

                  <span className="fs-3 fw-bold text-logo px-3">
                    {quantity}
                  </span>

                  <motion.button
                    type="button"
                    className="btn btn-outline-dark btn-lg"
                    onClick={() => handleQuantityChange('add')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ width: '45px', height: '45px' }}
                  >
                    +
                  </motion.button>
                </div>
              )}
            </div>

            {quantity > 0 && (
              <motion.div
                className="mb-4 p-3 bg-light rounded-3 border"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fs-5 fw-semibold text-dark">
                    Total ({quantity} {quantity === 1 ? 'item' : 'items'}):
                  </span>
                  <span className="fs-4 fw-bold text-success">
                    ${totalPrice}
                  </span>
                </div>
              </motion.div>
            )}

            <div className="mt-auto">
              <motion.button
                type="button"
                className="btn btn-dark btn-lg w-100 py-3 fw-semibold"
                onClick={handleOrderNow}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {quantity === 0 ? 'Add to Cart & Order' : 'Proceed to Checkout'}
              </motion.button>

              {/* <div className="text-center mt-3">
                <small className="text-muted">
                  <i className="bi bi-shield-check me-1"></i>
                  Secure checkout • 3 dollars transport fee 15 kilometres
                </small>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductDetail;
